import { action, computed, observable, makeAutoObservable } from 'mobx';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { EventStateActions } from 'src/domains/sportsbook/trading/view/eventDetails/eventSettings/EventActionsState/EventActionsState';
import { ParticipantModelType } from 'src_common/common/websocket2/modelsApi/Event';
import { timeout } from 'src_common/common/mobx-utils/timeout';
import { ParticipantsEventSettingsType } from './ParticipantsEventSettings';
import { ParticipantsType, SdkStaff } from 'src/AppState/sdk/staff';
import { MobxMap } from 'src_common/common/mobx-utils/MobxMap';
import { EventId } from 'src_common/common/websocket2/id/WebsocketId';

interface SmallParticipantModelType {
    id: string;
    name: string;
    role: string;
}

interface ParticipantUpdateType {
    id: string;
    name: string;
}

export class ParticipantsEventSettingsState {
    @observable public isEditMode: boolean = false;
    @observable public addedNewParticipants: Array<{ id: string; name: string }> = [];
    @observable public filter: string = '';
    @observable public newParticipantName: string = '';

    @observable private availableParticipantsResource: MobxMap<[string, string], ParticipantsType>;

    public constructor(
        private readonly getProps: () => ParticipantsEventSettingsType,
        private readonly sdkStaff: SdkStaff,
        private readonly eventStateActions: EventStateActions
    ) {
        this.availableParticipantsResource = new MobxMap(
            async ([sport, ngram]: [string, string]): Promise<ParticipantsType> => {
                if (ngram.trim() === '') {
                    return await this.sdkStaff.getParticipants({ requestBody: { sport } });
                }
                return await this.sdkStaff.getParticipants({ requestBody: { sport, ngram } });
            }
        );
        makeAutoObservable(this);
    }

    @computed private get props(): ParticipantsEventSettingsType {
        return this.getProps();
    }

    @computed private get eventId(): EventId {
        return this.props.eventId;
    }

    @computed private get eventModel(): EventModel | null {
        return this.eventId.getEventModel();
    }

    @computed private get participants(): Record<string, ParticipantModelType> {
        return this.eventModel?.participants ?? {};
    }

    @computed public get currentParticipantsList(): Array<SmallParticipantModelType> {
        const sorted = Object.values(this.participants).sort((a, b) => {
            const aName = a.name ?? null;
            const bName = b.name ?? null;
            if (aName === null || bName === null) {
                return 0;
            }
            return aName.localeCompare(bName);
        });

        return sorted
            .map((elem) => {
                return {
                    id: elem.idv2 ?? '', // participant id is no longer used, so idv2 is needed as the id when updating event participants
                    name: elem.name ?? '',
                    role: elem.role ?? '',
                };
            })
            .sort((a, b) => b.role.localeCompare(a.role));
    }

    @computed public get addedParticipants(): Array<SmallParticipantModelType> {
        const tempParticipants: Array<SmallParticipantModelType> = [];
        for (const participant of this.addedNewParticipants) {
            tempParticipants.push({
                id: participant.id,
                name: participant.name,
                role: '',
            });
        }

        return tempParticipants;
    }

    @computed private get availableParticipants(): ParticipantsType {
        if (this.eventModel !== null) {
            const resource = this.availableParticipantsResource.get([this.eventModel.sport, this.filter]).get();
            if (resource.type === 'ready') {
                return resource.value.slice(0, 100);
            }
        }
        return [];
    }

    @computed public get availableParticipantsList(): ParticipantsType {
        const currentIds = this.currentParticipantsList.map((elem) => elem.id.toString());
        return this.availableParticipants.filter((elem) => {
            return [...currentIds, ...this.addedParticipantsIds].includes(elem.id) === false;
        });
    }

    @computed private get prepareForSubmit(): { participants: Array<ParticipantUpdateType> } {
        const tempParticipants: Array<ParticipantUpdateType> = [];

        for (const participant of [...this.currentParticipantsList, ...this.addedParticipants]) {
            tempParticipants.push({
                id: participant.id,
                name: participant.name,
            });
        }

        return {
            participants: tempParticipants,
        };
    }

    @computed private get addedParticipantsIds(): Array<string> {
        return this.addedNewParticipants.map((elem) => elem.id);
    }

    @action public setIsEditMode = (): void => {
        this.isEditMode = !this.isEditMode;
        this.filter = '';
        this.addedNewParticipants = [];
    };

    @action public addParticipant = (participant: { id: string; name: string }): void => {
        this.addedNewParticipants.push(participant);
    };

    @action public removeParticipant = (participant: SmallParticipantModelType): void => {
        this.addedNewParticipants = this.addedNewParticipants.filter((elem) => {
            return elem.id !== participant.id;
        });
    };

    @action public setFilter = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this.filter = event.currentTarget.value;
    };

    @action public createParticipant = async (): Promise<void> => {
        if (this.newParticipantName.length > 0 && this.eventModel !== null) {
            const sport = this.eventModel.sport;
            const participant = await this.eventStateActions.createParticipant({
                name: this.newParticipantName,
                sport: sport,
                isCreation: true,
            });

            if (participant.status === 'error') {
                this.props.onSetErrorMessage(participant.message);
                return;
            } else {
                this.addParticipant(participant.value);
            }

            this.newParticipantName = '';
            await timeout(1000);
            await this.eventStateActions.participantsData.get([sport, this.filter]).refresh();
        }
    };

    @action public setNewParticipantName = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this.newParticipantName = event.currentTarget.value;
    };

    @action public submitParticipantsChanges = async (): Promise<void> => {
        const response = await this.sdkStaff.updateParticipants({
            event_id: this.eventId.toOldId(),
            requestBody: this.prepareForSubmit,
        });
        if (response.status === 'error') {
            this.props.onSetErrorMessage(response.message);
            return;
        }
        this.setIsEditMode();
    };
}
